import { useRef, useState } from "react";

export const MATERIAL_FILL = {
  acp: (c) => `linear-gradient(135deg, ${c}cc, ${c}88)`,
  hpl: () => "repeating-linear-gradient(90deg, #92400e 0 6px, #b45309 6px 9px, #a16207 9px 15px)",
  solid_metal: () => "repeating-linear-gradient(0deg, #9ca3af 0 2px, #a1a1aa 2px 4px, #d4d4d8 4px 5px)",
  gfrc: () => "radial-gradient(#57534e 1px, transparent 1.5px) 0 0/6px 6px, #a8a29e",
  metal_jalli: () => "radial-gradient(circle, #09090b 2.5px, transparent 3px) 0 0/10px 10px, #ca8a04",
  glazing: () => "linear-gradient(120deg, #0e749088 0%, #67e8f966 45%, #0e749088 55%, #164e6388 100%)",
  curtain_wall: () => "repeating-linear-gradient(0deg, #064e3b 0 2px, transparent 2px 28px), repeating-linear-gradient(90deg, #064e3b 0 2px, transparent 2px 22px), linear-gradient(120deg, #0f766e99, #5eead466 50%, #115e5999)",
};

const pct = (e, el) => {
  const r = el.getBoundingClientRect();
  return { x: Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100)), y: Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100)) };
};

const ZoneBox = ({ z, mat, selected, rendered, onSelect, passive }) => {
  const color = mat?.color || "#71717a";
  const style = { left: `${z.x}%`, top: `${z.y}%`, width: `${z.w}%`, height: `${z.h}%`, borderColor: color };
  if (rendered && mat) style.background = MATERIAL_FILL[mat.id](color);
  else style.background = `${color}22`;
  return (
    <button
      type="button"
      data-testid={`zone-box-${z.id}`}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onSelect(z.id); }}
      style={style}
      className={`absolute border-2 text-left transition-[box-shadow,opacity] duration-200 ${passive ? "pointer-events-none " : ""}${selected ? "z-20 shadow-[0_0_0_3px_rgba(14,165,233,0.7)]" : "z-10 hover:shadow-[0_0_0_2px_rgba(255,255,255,0.4)]"}`}
    >
      <span className="absolute left-0 top-0 max-w-full truncate px-1.5 py-0.5 font-mono text-[10px] font-medium text-white" style={{ background: color }}>
        {z.label}{mat ? ` · ${mat.short}` : ""}
      </span>
    </button>
  );
};

export default function ZoneCanvas({ imageUrl, zones, materials, selectedId, onSelect, drawMode, onDrawn, rendered, scaleMode, scalePts = [], onPoint }) {
  const ref = useRef(null);
  const [draft, setDraft] = useState(null);

  const down = (e) => {
    if (scaleMode) return onPoint(pct(e, ref.current));
    if (!drawMode) return onSelect(null);
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = pct(e, ref.current);
    setDraft({ sx: p.x, sy: p.y, x: p.x, y: p.y, w: 0, h: 0 });
  };
  const move = (e) => {
    if (!draft) return;
    const p = pct(e, ref.current);
    setDraft((d) => ({ ...d, x: Math.min(d.sx, p.x), y: Math.min(d.sy, p.y), w: Math.abs(p.x - d.sx), h: Math.abs(p.y - d.sy) }));
  };
  const up = () => {
    if (draft && draft.w > 1 && draft.h > 1) onDrawn({ x: draft.x, y: draft.y, w: draft.w, h: draft.h });
    setDraft(null);
  };

  return (
    <div className="relative w-full select-none overflow-hidden rounded-md border border-zinc-800 bg-white" data-testid="zone-canvas">
      <img src={imageUrl} alt="Elevation drawing" className="block w-full" draggable={false} data-testid="elevation-image" />
      <div
        ref={ref}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        className={`absolute inset-0 ${drawMode || scaleMode ? "cursor-crosshair" : "cursor-default"}`}
        data-testid="zone-canvas-overlay"
      >
        {zones.map((z) => (
          <ZoneBox key={z.id} z={z} mat={materials.find((m) => m.id === z.material_id)} selected={z.id === selectedId} rendered={rendered} onSelect={onSelect} passive={drawMode || scaleMode} />
        ))}
        {scalePts.length > 0 && (
          <svg className="pointer-events-none absolute inset-0 z-30 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="scale-overlay">
            {scalePts.length === 2 && <line x1={scalePts[0].x} y1={scalePts[0].y} x2={scalePts[1].x} y2={scalePts[1].y} stroke="#f59e0b" strokeWidth="0.4" vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2 }} />}
            {scalePts.map((p) => <circle key={`${p.x},${p.y}`} cx={p.x} cy={p.y} r="0.6" fill="#f59e0b" />)}
          </svg>
        )}
        {draft && (
          <div className="absolute z-30 border-2 border-dashed border-amber-400 bg-amber-400/10"
            style={{ left: `${draft.x}%`, top: `${draft.y}%`, width: `${draft.w}%`, height: `${draft.h}%` }} />
        )}
      </div>
    </div>
  );
}
